import type { SidebarAccountScrollContainerProps } from "@bafe/ui";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "../lib/utils.js";
import { ScrollBar } from "./ui/scroll-area.js";

const HIDE_DELAY_MS = 700;

export function AccountWorkspaceScroll({
	children,
	className,
	role,
	"aria-label": ariaLabel,
}: SidebarAccountScrollContainerProps) {
	const [scrollbarVisible, setScrollbarVisible] = useState(false);
	const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const viewportRef = useRef<HTMLDivElement>(null);

	const revealScrollbar = useCallback(() => {
		setScrollbarVisible(true);
		if (hideTimer.current) clearTimeout(hideTimer.current);
		hideTimer.current = setTimeout(() => setScrollbarVisible(false), HIDE_DELAY_MS);
	}, []);

	useEffect(() => {
		return () => {
			if (hideTimer.current) clearTimeout(hideTimer.current);
		};
	}, []);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;
		const onScroll = () => revealScrollbar();
		viewport.addEventListener("scroll", onScroll, { passive: true });
		return () => viewport.removeEventListener("scroll", onScroll);
	}, [revealScrollbar]);

	return (
		<ScrollAreaPrimitive.Root
			className={cn(
				"account-workspace-scroll relative max-h-[min(240px,40vh)] overflow-hidden border-b border-(--border-subtle)",
				className,
			)}
			onPointerEnter={revealScrollbar}
			onFocusCapture={revealScrollbar}
		>
			<ScrollAreaPrimitive.Viewport
				ref={viewportRef}
				className="size-full max-h-[min(240px,40vh)] [&>div]:pb-0.5"
				onWheel={revealScrollbar}
				onTouchMove={revealScrollbar}
			>
				<div role={role} aria-label={ariaLabel}>
					{children}
				</div>
			</ScrollAreaPrimitive.Viewport>
			<ScrollBar
				orientation="vertical"
				className={cn(
					"account-workspace-scroll-bar w-3 transition-opacity duration-300 ease-out",
					scrollbarVisible ? "opacity-100" : "pointer-events-none opacity-0",
				)}
			/>
			<ScrollAreaPrimitive.Corner />
		</ScrollAreaPrimitive.Root>
	);
}
